import {
  WorkflowTemplateApiVersion,
  type WorkflowTemplateType,
} from "../schemas/WorkflowTemplateSchemas.ts";
import { sanitizeWorkflowTemplateV2UpdatePayload } from "../util/sanitizeWorkflowTemplateV2UpdatePayload.ts";
import {
  fetchAndCollate,
  getFilterObject,
  type GetListResponseType,
  type ResourceAdapterType,
  type UpdateOneRequestType,
} from "../util/index.ts";
import { restAPI } from "./api.ts";

export type WorkflowTemplateWithOwnerAndCreatedByNameType =
  WorkflowTemplateType & {
    workflow_owner_name?: string;
    created_by_name?: string;
  };

export type WorkflowTemplateAdapterType = Pick<
  ResourceAdapterType<WorkflowTemplateType>,
  "getList" | "getOne" | "updateOne"
>;

export type WorkflowTemplatesV2AdapterType = Pick<
  ResourceAdapterType<WorkflowTemplateType>,
  "updateOne"
>;

export const WorkflowTemplatesAdapter: WorkflowTemplateAdapterType = {
  /**
   * Get the complete list of Workflow Templates
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/workflow_templates/search_workflow_templates
   */
  getList: async ({ page, pageSize, sortBy, sortDirection, filters }) => {
    const filterObject = getFilterObject(filters);
    const defaultApiVersionFilter = {
      api_version: {
        $in: [WorkflowTemplateApiVersion.V1, WorkflowTemplateApiVersion.V2],
      },
    };

    return fetchAndCollate(
      async ({ page, pageSize }) =>
        await restAPI.nodeAPI.POST<GetListResponseType<WorkflowTemplateType>>({
          version: 1,
          endpoint: "/workflow_templates/search",
          body: {
            sort_by: sortBy,
            sort_direction: sortDirection,
            page: page,
            page_size: pageSize,
            filter: Object.hasOwn(filterObject, "api_version")
              ? filterObject
              : { ...filterObject, ...defaultApiVersionFilter },
          },
        }),
      page,
      pageSize
    );
  },
  /**
   * Get the detail info Workflow Templates
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/workflow_templates/get_workflow_template
   */
  getOne: async ({ id }) => {
    const resp = await restAPI.nodeAPI.GET<WorkflowTemplateType>({
      version: 1,
      endpoint: `/workflow_templates/${id}`,
    });
    return { data: resp };
  },

  /**
   * Update a workflow template
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/workflow_templates/put_workflow_template
   */
  updateOne: async ({
    id,
    data,
  }: UpdateOneRequestType<Partial<WorkflowTemplateType>>) => {
    const resp = await restAPI.nodeAPI.PUT<WorkflowTemplateType>({
      version: 1,
      endpoint: `/workflow_templates/${id}`,
      body: data,
    });

    return {
      data: resp,
    };
  },
};

/** A single entry from the generic system action catalog. */
export type GenericSystemActionCatalogEntryType = {
  operation: string;
  display_name: string;
  configuration:
    | {
        execution_type: "api";
        method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
        path: string;
        path_params: Array<{ id: string; required: boolean }>;
        query_params: Array<{ id: string; required: boolean }>;
      }
    | {
        execution_type: "action";
      };
};

export const GenericSystemActionCatalogAdapter = {
  /** Fetch the full allowlisted catalog. */
  getList: async (): Promise<GenericSystemActionCatalogEntryType[]> => {
    return restAPI.nodeAPI.GET<GenericSystemActionCatalogEntryType[]>({
      version: 1,
      endpoint: "/workflow_templates/generic_system_action/catalog",
    });
  },
};

/**
 * v2 workflow template endpoints (asset-decoupled templates).
 * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/workflow_templates/update_workflow_template_v2
 */
export const WorkflowTemplatesV2Adapter: WorkflowTemplatesV2AdapterType = {
  updateOne: async ({
    id,
    data,
  }: UpdateOneRequestType<Partial<WorkflowTemplateType>>) => {
    const resp = await restAPI.nodeAPI.PUT<WorkflowTemplateType>({
      version: 2,
      endpoint: `/workflow_templates/${id}`,
      body: sanitizeWorkflowTemplateV2UpdatePayload(data),
    });

    return {
      data: resp,
    };
  },
};
